import { useEffect, useState } from "react";
import { LineChart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SCORES = [
  { key: "maturity",    label: "Maturité",    color: "#0B2545" },
  { key: "trajectory",  label: "Trajectoire", color: "#C5A880" },
  { key: "progression", label: "Progression", color: "#0B7A3B" },
  { key: "execution",   label: "Exécution",   color: "#B45309" },
  { key: "potential",   label: "Potentiel",   color: "#7C3AED" },
];

export default function AbiPassportTimeline({ days = 90 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(Object.fromEntries(SCORES.map(s => [s.key, true])));

  useEffect(() => {
    api.get(`/abi/copilot/passport/timeline?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData({ snapshots: [], deltas: {}, has_history: false }))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div className="bg-white rounded-lg shadow-sm p-5 flex items-center gap-2 text-sm text-slate-500"
         data-testid="abi-passport-timeline-loading">
      <Loader2 size={14} className="animate-spin" /> Chargement de la timeline…
    </div>
  );

  const snapshots = data?.snapshots || [];
  const hasHistory = snapshots.length >= 2;

  return (
    <div className="bg-white rounded-lg shadow-sm p-5" data-testid="abi-passport-timeline">
      <div className="flex items-center gap-2 mb-1">
        <LineChart size={18} style={{ color: "#0B2545" }} />
        <h3 className="font-serif text-xl" style={{ color: "#0B2545" }}>
          Timeline de progression · {days} derniers jours
        </h3>
      </div>
      <p className="text-[12px] italic text-slate-600 mb-4">
        Preuve chiffrée de votre trajectoire — chaque point est un snapshot quotidien.
      </p>

      {!hasHistory ? (
        <div className="p-4 rounded text-[13px] text-slate-700"
             style={{ background: "#FBF6EA", border: "1px dashed #C5A880" }}
             data-testid="abi-passport-timeline-empty">
          {snapshots.length === 0
            ? "Aucun snapshot enregistré pour l'instant. Revenez chaque jour — votre progression s'affichera ici."
            : "Un seul snapshot pour l'instant. Dès demain, votre timeline commencera à se dessiner."}
        </div>
      ) : (
        <>
          <TimelineChart snapshots={snapshots} visible={visible} />

          {/* Legend + toggles */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="abi-passport-timeline-legend">
            {SCORES.map(s => (
              <button
                key={s.key}
                onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
                data-testid={`abi-timeline-toggle-${s.key}`}
                className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded transition"
                style={{
                  background: visible[s.key] ? s.color : "#F3F4F6",
                  color:      visible[s.key] ? "#FFFFFF" : "#6B7280",
                  opacity:    visible[s.key] ? 1 : 0.6,
                }}>
                <span className="w-2 h-2 rounded-full inline-block"
                      style={{ background: visible[s.key] ? "#FFFFFF" : s.color }} />
                {s.label}
                {data.deltas && data.deltas[s.key] !== undefined && (
                  <span className="ml-1 opacity-90">
                    ({data.deltas[s.key] >= 0 ? "+" : ""}{data.deltas[s.key].toFixed(1)})
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** SVG multi-line chart — minimal, dependency-free. */
function TimelineChart({ snapshots, visible }) {
  const W = 720, H = 220, PAD_L = 34, PAD_R = 12, PAD_T = 12, PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = snapshots.length;
  const xAt = (i) => PAD_L + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (v) => PAD_T + innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;

  const yTicks = [0, 25, 50, 75, 100];
  const first = snapshots[0]?.date || "";
  const last  = snapshots[snapshots.length - 1]?.date || "";

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-w-full"
           style={{ minHeight: 220 }}
           data-testid="abi-passport-timeline-chart">
        {/* Grid + Y ticks */}
        {yTicks.map(t => (
          <g key={t}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(t)} y2={yAt(t)}
                  stroke="#E5E7EB" strokeDasharray="2 3" />
            <text x={PAD_L - 6} y={yAt(t) + 3} fontSize="9" textAnchor="end" fill="#6B7280">
              {t}
            </text>
          </g>
        ))}
        {/* X labels (first + last) */}
        <text x={PAD_L} y={H - 8} fontSize="9" fill="#6B7280">{first}</text>
        <text x={W - PAD_R} y={H - 8} fontSize="9" fill="#6B7280" textAnchor="end">{last}</text>

        {/* Lines */}
        {SCORES.filter(s => visible[s.key]).map(s => {
          const points = snapshots.map((snap, i) => `${xAt(i)},${yAt(snap[s.key] || 0)}`).join(" ");
          return (
            <g key={s.key}>
              <polyline points={points} fill="none" stroke={s.color} strokeWidth="2"
                        strokeLinejoin="round" strokeLinecap="round" />
              {snapshots.map((snap, i) => (
                <circle key={i} cx={xAt(i)} cy={yAt(snap[s.key] || 0)} r="2.5"
                        fill={s.color}>
                  <title>{`${snap.date} · ${s.label} = ${(snap[s.key] || 0).toFixed(1)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
